export const LEGACY_APPLICATION_SHOW_ROUTE = 'authenticated.tas.applications.show';
export const MANAGE_ROUTE = 'authenticated.tas.applications.manage';

// The program types that have a manage screen. Values match the
// typeClassifications handled in `app/routes/authenticated/tas/applications/show.ts`'s
// afterModel routeMappings (which route every application to its `-guided` variant for
// the `new` flow) — manage itself is a single shared route, not one per guided group.
const MANAGEABLE_TYPES = new Set([
  'TAS.ProgramType.2',
  'TAS.ProgramType.3',
  'TAS.ProgramType.4.2',
  'TAS.ProgramType.4.3',
]);

/**
 * Resolves the route for viewing an existing TAS application: the shared `manage`
 * screen when `tasCourseManagement` is on and the application's program type is
 * manageable, otherwise the legacy show route.
 */
export function applicationDetailRoute(
  typeClassification: string | null | undefined,
  isManageScreenEnabled: boolean
): string {
  if (!isManageScreenEnabled) {
    return LEGACY_APPLICATION_SHOW_ROUTE;
  }
  const isManageable = typeClassification ? MANAGEABLE_TYPES.has(typeClassification) : false;
  return isManageable ? MANAGE_ROUTE : LEGACY_APPLICATION_SHOW_ROUTE;
}

/** Structural minimum of a program-instance record: enough to say what type it
 * is. Satisfied by `LiveTasProgram` (reimagined-data) among others. */
export interface ProgramInstanceType {
  id: string;
  typeClassification: string | null;
}

/**
 * `programInstanceId → typeClassification`, for the activity surfaces (TAS
 * dashboard, dependent programs) whose application records are
 * `LiveTasApplication` and carry no typeClassification of their own — the detail
 * route needs one, and the sibling program list is where it lives.
 *
 * Takes the RAW program list on purpose. Those components each keep a narrowed
 * `programs` getter for their own card rendering (active Type-4 only; dependent-
 * owned only), and deriving from either would silently route whole program types
 * to the legacy page. Callers should wrap this in a `@cached` getter — it is
 * consulted once per rendered card.
 */
export function typeClassificationsByProgramInstance(
  programs: readonly ProgramInstanceType[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const program of programs) {
    if (program.typeClassification) {
      map.set(program.id, program.typeClassification);
    }
  }
  return map;
}

/**
 * `applicationDetailRoute` for an application known only by its program-instance
 * id — pair with `typeClassificationsByProgramInstance`. An application whose
 * instance isn't in the map (or has no instance) resolves to the legacy show
 * route, which is also where an unmanageable type lands.
 */
export function applicationDetailRouteForInstance(
  programInstanceId: string | null | undefined,
  typesByInstance: ReadonlyMap<string, string>,
  isManageScreenEnabled: boolean
): string {
  const typeClassification = programInstanceId ? typesByInstance.get(programInstanceId) : undefined;
  return applicationDetailRoute(typeClassification, isManageScreenEnabled);
}
